import { createInternalNeonAuth } from '@neondatabase/neon-js/auth';

// Talks to the Forge Cloud API. People sign in with Neon Auth (managed Better Auth); the API receives the
// session's JWT as a bearer token. Without VITE_NEON_AUTH_URL the console uses the API's dev sign-in.
export const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8787').replace(/\/+$/, '');
const NEON_AUTH_URL = import.meta.env.VITE_NEON_AUTH_URL || '';

// createInternalNeonAuth gives both the Better Auth client and getJWTToken (the session's JWT).
const neon = NEON_AUTH_URL ? createInternalNeonAuth(NEON_AUTH_URL) : null;
export const auth = neon?.adapter || null;
export const authMode = auth ? 'neon' : 'dev';

const DEV_KEY = 'forge.dev.token';
const store = {
  get: () => { try { return localStorage.getItem(DEV_KEY); } catch { return null; } },
  set: v => { try { v ? localStorage.setItem(DEV_KEY, v) : localStorage.removeItem(DEV_KEY); } catch { /* storage blocked */ } },
};

export async function getToken() {
  if (!auth) return store.get();
  return (await neon.getJWTToken()) || null;
}

export async function getSessionUser() {
  if (!auth) return store.get() ? { dev: true } : null;
  const r = await auth.getSession();
  return r?.data?.user || null;
}

export async function signIn(email, password) {
  if (!auth) {
    const r = await request('/v1/dev/login', { method: 'POST', json: { email }, anonymous: true });
    store.set(r.token);
    return r.user;
  }
  const r = await auth.signIn.email({ email, password });
  if (r.error) throw new Error(r.error.message || 'Sign-in failed');
  return r.data?.user;
}

export async function signUp(name, email, password) {
  const r = await auth.signUp.email({ name, email, password });
  if (r.error) throw new Error(r.error.message || 'Sign-up failed');
  return r.data?.user;
}

// A Forge Code sign-in in progress (/device?code=…). Social sign-in leaves the console and Neon Auth sends people back
// to the home page, without the code, so it is kept here until they authorize or cancel it.
const PENDING_KEY = 'forge.pendingDevice';
export const pendingDevice = {
  get() {
    try { const p = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); return p && Date.now() - p.at < 15 * 60_000 ? p.code : null; } catch { return null; }
  },
  set(code) { try { localStorage.setItem(PENDING_KEY, JSON.stringify({ code, at: Date.now() })); } catch { /* storage blocked */ } },
  clear() { try { localStorage.removeItem(PENDING_KEY); } catch { /* storage blocked */ } },
};

// Where to go back to after a social sign-in started on a page that needed one (an artifact link).
const RETURN_KEY = 'forge.returnTo';
export const returnTo = {
  set(path) { try { localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* storage blocked */ } },
  take() {
    try { const p = JSON.parse(localStorage.getItem(RETURN_KEY) || 'null'); localStorage.removeItem(RETURN_KEY); return p && Date.now() - p.at < 15 * 60_000 ? p.path : null; } catch { return null; }
  },
};

export async function signInSocial(provider) {
  const code = new URLSearchParams(window.location.search).get('code');
  if (window.location.pathname === '/device' && code) pendingDevice.set(code);
  await auth.signIn.social({ provider, callbackURL: window.location.href });
}

export async function signOut() {
  if (auth) await auth.signOut();
  store.set(null);
}

export class ApiError extends Error {
  constructor(status, message) { super(message); this.status = status; }
}

export async function request(path, { method = 'GET', json, anonymous = false, raw = false, signal } = {}) {
  const headers = {};
  if (!anonymous) {
    const token = await getToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  if (json !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(`${API_URL}${path}`, { method, headers, body: json !== undefined ? JSON.stringify(json) : undefined, signal });
  if (raw) return res;
  const text = await res.text();
  let data = text;
  try { data = JSON.parse(text); } catch { /* text */ }
  if (!res.ok) throw new ApiError(res.status, data?.error?.message || `HTTP ${res.status}`);
  return data;
}

/** Follow a cloud session's Server-Sent Events (fetch-based, so the bearer header can be sent). */
export async function followSession(id, onEvent, signal) {
  const res = await request(`/v1/sessions/${id}/events`, { raw: true, signal });
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += decoder.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = { event: 'message', data: '' };
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) ev.event = line.slice(6).trim();
        else if (line.startsWith('data:')) ev.data += (ev.data ? '\n' : '') + line.slice(5).replace(/^ /, '');
        else if (line.startsWith('id:')) ev.id = line.slice(3).trim();
      }
      if (ev.event !== 'ping') onEvent(ev);
    }
  }
}

export const fmt = {
  tokens: n => (n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${+(n / 1e3).toFixed(1)}k` : String(n ?? 0)),
  usd: n => `$${(n ?? 0).toFixed(n >= 100 ? 0 : 2)}`,
  date: d => (d ? new Date(d).toLocaleString() : '—'),
  ago: d => {
    if (!d) return '—';
    const s = Math.round((Date.now() - new Date(d)) / 1000);
    return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)}m ago` : s < 86400 ? `${Math.round(s / 3600)}h ago` : `${Math.round(s / 86400)}d ago`;
  },
};
